import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["ticketId", "threadId"];
import { registerMutation, useMutation } from 'data-fetching-client';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { convertToHtml, convertToPlaintext } from 'hubspot-prosemirror/prosemirror/ProsemirrorUtils';
import { GET_TICKET_THREAD, GET_TICKET_THREAD_KEY } from './useTicketThread';
import { GET_THREAD_ATTACHMENTS, GET_THREAD_ATTACHMENTS_KEY } from './useThreadAttachments';
import { useTicketThread } from './useTicketThread';
import { MESSAGE_CHAR_LIMIT, UnpublishableReasonEnum } from '../constants';
import { FileUploadStatus } from './types';
import { api } from '../utils/api';
import { PUBLISH_MESSAGE_BASE_PATH } from '../../api/paths';
const publishMessage = async _ref => {
  let {
      ticketId,
      threadId
    } = _ref,
    payload = _objectWithoutPropertiesLoose(_ref, _excluded);
  const response = await api(`${PUBLISH_MESSAGE_BASE_PATH}/${ticketId}/${threadId || 'new'}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json;charset=UTF-8',
      'X-HS-User-Request': '1'
    },
    body: JSON.stringify(payload)
  });
  if (!response.ok) {
    throw new Error(`Publish message error: ${response.status} - ${response.statusText}`);
  }
  return response.json();
};
const PUBLISH_MESSAGE_MUTATION_FIELD_NAME = 'publishMessage';
const PUBLISH_MESSAGE_MUTATION = registerMutation({
  fieldName: PUBLISH_MESSAGE_MUTATION_FIELD_NAME,
  fetcher: publishMessage,
  args: ['ticketId', 'threadId', 'message', 'richMessage', 'fileIds', 'inReplyToId']
});
export function usePublishMessage({
  ticketId,
  threadId,
  richEditorContent,
  uploads,
  clearEditor
}) {
  const history = useHistory();
  const {
    lastMessageId: inReplyToId
  } = useTicketThread(ticketId, threadId || '');
  const [mutate, {
    loading,
    error
  }] = useMutation(PUBLISH_MESSAGE_MUTATION, {
    update: (cache, {
      data
    }) => {
      if (!threadId) {
        return;
      }
      if (!(data !== null && data !== void 0 && data[PUBLISH_MESSAGE_MUTATION_FIELD_NAME])) {
        return;
      }
      const result = data[PUBLISH_MESSAGE_MUTATION_FIELD_NAME];
      const {
        message
      } = result;
      const uploadedFiles = uploads.filter(upload => upload.status === FileUploadStatus.UPLOADED);
      cache.updateQuery({
        query: GET_TICKET_THREAD,
        variables: {
          ticketId,
          threadId
        }
      }, existingData => {
        const cachedThread = existingData === null || existingData === void 0 ? void 0 : existingData[GET_TICKET_THREAD_KEY];
        if (cachedThread) {
          var _cachedThread$pagedTh;
          const updatedAttachmentsMap = Object.assign({}, cachedThread.attachmentsMap);
          if (uploadedFiles.length > 0) {
            uploadedFiles.forEach(file => {
              if (!cachedThread.attachmentsMap[file.id]) {
                updatedAttachmentsMap[file.id] = {
                  attachmentTimestamp: Date.now(),
                  fileMetadata: {
                    expiresAt: file.expiresAt,
                    url: file.downloadUrl,
                    name: file.fileName,
                    extension: file.fileExtension,
                    type: file.fileType,
                    size: file.fileSize,
                    height: file.imageHeight,
                    width: file.imageWidth
                  }
                };
              }
            });
          }
          const updatedThread = Object.assign({}, cachedThread, {
            attachmentsMap: Object.assign({}, updatedAttachmentsMap),
            pagedThreadHistory: Object.assign({}, cachedThread.pagedThreadHistory, {
              results: [message, ...(((_cachedThread$pagedTh = cachedThread.pagedThreadHistory) === null || _cachedThread$pagedTh === void 0 ? void 0 : _cachedThread$pagedTh.results) || [])]
            })
          });
          return {
            [GET_TICKET_THREAD_KEY]: updatedThread
          };
        }
        return existingData;
      });
      cache.updateQuery({
        query: GET_THREAD_ATTACHMENTS,
        variables: {
          ticketId,
          threadId
        }
      }, existingData => {
        const cachedAttachments = existingData === null || existingData === void 0 ? void 0 : existingData[GET_THREAD_ATTACHMENTS_KEY];
        if (!cachedAttachments || uploadedFiles.length === 0) {
          return existingData;
        }
        const existingAttachmentsList = cachedAttachments.attachmentsList;
        const latestAttachmentTimestamp = existingAttachmentsList.reduce((latestTimestamp, attachment) => Math.max(latestTimestamp, attachment.attachmentTimestamp), 0);
        if (message.timestamp <= latestAttachmentTimestamp) {
          return existingData;
        }
        const newAttachments = uploadedFiles.map(file => ({
          attachmentTimestamp: message.timestamp,
          fileMetadata: {
            expiresAt: file.expiresAt,
            url: file.downloadUrl,
            name: file.fileName,
            extension: file.fileExtension,
            type: file.fileType,
            size: file.fileSize,
            height: file.imageHeight,
            width: file.imageWidth
          }
        }));
        return {
          [GET_THREAD_ATTACHMENTS_KEY]: Object.assign({}, cachedAttachments, {
            attachmentsList: [...newAttachments, ...existingAttachmentsList]
          })
        };
      });
    },
    onCompleted: () => {
      clearEditor();
      if (!threadId) {
        const params = new URLSearchParams(history.location.search);
        params.set('creatingThread', '1');
        history.replace({
          pathname: `/ticket/${ticketId}`,
          search: `?${params.toString()}`
        });
      }
    }
  });
  const [isPublishable, setIsPublishable] = useState(false);
  const [unpublishableReason, setUnpublishableReason] = useState(UnpublishableReasonEnum.NO_CONTENT);
  const handlePublishMessage = useCallback(() => {
    if (!isPublishable || !richEditorContent) {
      return;
    }
    const htmlContent = convertToHtml(richEditorContent);
    const plaintextContent = convertToPlaintext(richEditorContent);
    const fileIds = uploads.filter(upload => upload.status === FileUploadStatus.UPLOADED).map(upload => upload.id);
    const payload = Object.assign({
      ticketId,
      threadId,
      inReplyToId,
      fileIds,
      message: plaintextContent
    }, htmlContent && {
      richMessage: htmlContent
    });
    return mutate({
      variables: payload
    });
  }, [inReplyToId, ticketId, threadId, isPublishable, mutate, richEditorContent, uploads]);
  useEffect(() => {
    const plainTextContent = convertToPlaintext(richEditorContent);
    const htmlContent = convertToHtml(richEditorContent);
    const hasMessage = plainTextContent.trim() !== '';
    const hasUploadedFiles = uploads.some(upload => upload.status === FileUploadStatus.UPLOADED);
    const hasUploadingFiles = uploads.some(upload => upload.status === FileUploadStatus.UPLOADING || upload.status === FileUploadStatus.PENDING);
    const hasFailedUploads = uploads.some(upload => upload.status === FileUploadStatus.ERROR);
    const validMessageLength = htmlContent.length <= MESSAGE_CHAR_LIMIT;
    let unpublishable = null;
    if (!(hasMessage || hasUploadedFiles)) {
      unpublishable = UnpublishableReasonEnum.NO_CONTENT;
    } else if (hasFailedUploads) {
      unpublishable = UnpublishableReasonEnum.HAS_FAILED_UPLOADS;
    } else if (hasUploadingFiles) {
      unpublishable = UnpublishableReasonEnum.UPLOADING;
    } else if (!validMessageLength) {
      unpublishable = UnpublishableReasonEnum.EXCEEDS_CHAR_LIMIT;
    }
    setUnpublishableReason(unpublishable);
    setIsPublishable(unpublishable === null);
  }, [richEditorContent, uploads]);
  return {
    publishMessage: handlePublishMessage,
    publishable: {
      isPublishable,
      unpublishableReason
    },
    isPublishingMessage: loading,
    error
  };
}